// src/pages/DeleteAccount.js
// Page de suppression de compte - conforme exigences Google Play / Apple

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../hooks/useTheme';
import apiClient from '../api/client';

const DeleteAccount = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isConfirmValid = confirmText.trim().toUpperCase() === (t('deleteAccount.confirmPlaceholder') || 'DELETE').toUpperCase();

  const handleDelete = async () => {
    if (!isConfirmValid) return;
    setLoading(true);
    setError('');
    try {
      await apiClient.post('/endpoints/account.php?action=delete', { password });
      localStorage.clear();
      navigate('/');
    } catch (err) {
      setError(t('deleteAccount.error'));
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: theme.bg },
    header: {
      display: 'flex', alignItems: 'center', gap: '12px', padding: '16px',
      position: 'sticky', top: 0, backgroundColor: theme.bg, zIndex: 50, borderBottom: `1px solid ${theme.borderSoft}`,
    },
    backButton: { background: 'none', border: 'none', color: theme.textPrimary, fontSize: '20px', cursor: 'pointer' },
    title: { fontSize: '18px', fontWeight: '700', color: theme.textPrimary },
    content: { padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: '20px' },
    warningBox: {
      padding: '16px', backgroundColor: 'rgba(245, 101, 101, 0.1)', border: '1px solid rgba(245, 101, 101, 0.3)',
      borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '8px',
    },
    warningTitle: { fontSize: '15px', fontWeight: '700', color: '#F56565' },
    warningText: { fontSize: '14px', color: theme.textSecondary, lineHeight: '1.5' },
    label: { fontSize: '13px', fontWeight: '600', color: theme.textPrimary, marginBottom: '6px' },
    input: {
      width: '100%', padding: '12px 14px', backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`,
      borderRadius: '10px', color: theme.textPrimary, fontSize: '14px', outline: 'none', boxSizing: 'border-box',
    },
    errorText: { fontSize: '13px', color: '#F56565' },
    deleteButton: {
      padding: '14px', backgroundColor: '#F56565', border: 'none', borderRadius: '10px', color: '#FFFFFF',
      fontSize: '15px', fontWeight: '700', cursor: 'pointer', opacity: isConfirmValid ? 1 : 0.5,
    },
    cancelButton: {
      padding: '14px', backgroundColor: 'transparent', border: `1px solid ${theme.border}`, borderRadius: '10px',
      color: theme.textPrimary, fontSize: '15px', fontWeight: '600', cursor: 'pointer',
    },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>{t('deleteAccount.title')} | Fynho</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>←</button>
        <div style={styles.title}>{t('deleteAccount.title')}</div>
      </div>

      <div style={styles.content}>
        <div style={styles.warningBox}>
          <div style={styles.warningTitle}>⚠️ {t('deleteAccount.warning')}</div>
          <div style={styles.warningText}>{t('deleteAccount.warningText')}</div>
        </div>

        <div>
          <div style={styles.label}>{t('deleteAccount.passwordRequired')}</div>
          <input
            type="password"
            style={styles.input}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <div>
          <div style={styles.label}>{t('deleteAccount.confirmLabel')}</div>
          <input
            type="text"
            style={styles.input}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={t('deleteAccount.confirmPlaceholder')}
          />
        </div>

        {error && <div style={styles.errorText}>{error}</div>}

        <button
          style={styles.deleteButton}
          onClick={handleDelete}
          disabled={!isConfirmValid || loading}
        >
          {loading ? t('common.loading') : t('deleteAccount.confirmButton')}
        </button>

        <button style={styles.cancelButton} onClick={() => navigate(-1)}>
          {t('deleteAccount.cancelButton')}
        </button>
      </div>
    </div>
  );
};

export default DeleteAccount;